import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { Conditional } from '@resurs/react-utils'
import { Switch } from '@resurs/midas-react'
import { getText } from '../../../../../internal/TextLabels'
import {
  isExistingAccountPaymentMethod,
  PaymentMethods,
} from '../../../../../models/payment-methods'
import { getDeepOr } from '../../../../../util/helpers'
import { getPaymentMethodDescription } from '../../../../../util/payApplyUtils'
import Frame from '../../shared/Frame'
import PaymentMethodOption from './PaymentMethodOption'
import PaymentMethodsGroup from './PaymentMethodsGroup'

const groupPaymentMethods = (paymentMethods = [], hideDisabledResurs = true) =>
  paymentMethods
    .filter(pm => !pm.disabledResurs || !hideDisabledResurs)
    .reduce(
      (grouped, pm) =>
        isExistingAccountPaymentMethod(pm)
          ? {
              ...grouped,
              existing: grouped.existing.concat(pm),
            }
          : { ...grouped, others: grouped.others.concat(pm) },
      {
        existing: [],
        others: [],
      },
    )

const getAccountNumber = pm => getDeepOr('', pm, 'accounts.0.accountNumber')

const renderPaymentMethodOption = (method, selected, onChange) => {
  const props = {
    id:
      method.id +
      (method.type === PaymentMethods.RESURS_PART_PAYMENT
        ? getAccountNumber(method)
        : ''),
    type: method.type,
    text: getPaymentMethodDescription(method),
    name: 'paymentMethods',
    checked: selected,
    disabled: method.status.disabled,
    onChange: () => onChange(method, false),
    key: method.id + getAccountNumber(method),
    ...(method.disabledResurs && {
      disabledReasons: method.status.disabledReasons,
    }),
  }
  return <PaymentMethodOption {...props} />
}

const renderPendingApplicationOption = (selected, onChange) => {
  const props = {
    id: 'pending-applications-list',
    type: 'PENDING',
    text: getText('payApply.paymentMethods.pendingApplications.label'),
    name: 'pendingApplications',
    checked: selected,
    onChange: () => onChange(null, { pendingApplications: true }),
  }

  return <PaymentMethodOption {...props} />
}

const PaymentMethodPicker = ({
  paymentMethods,
  hasPendingApplications,
  onChange,
  selectedPaymentMethod,
  pendingSelected,
}) => {
  const [{ existing, others }, setGrouped] = useState({
    existing: [],
    others: [],
  })
  const [disabledCount, setDisabledCount] = useState(0)
  const [showDisabled, setShowDisabled] = useState(false)

  useEffect(() => {
    setDisabledCount(paymentMethods?.filter(pm => pm.disabledResurs)?.length)
  }, [])

  useEffect(
    () => setGrouped(groupPaymentMethods(paymentMethods, !showDisabled)),
    [paymentMethods, showDisabled],
  )

  return (
    <div>
      <Frame style={{ backgroundColor: '#fff', marginTop: 0 }}>
        <Conditional when={existing.length}>
          <PaymentMethodsGroup
            showHeading={!!others.length}
            heading={getText('payApply.paymentMethods.existingAccountsHeading')}
          >
            {existing.map(pm =>
              renderPaymentMethodOption(
                pm,
                (selectedPaymentMethod || {}).id === pm.id &&
                  getAccountNumber(selectedPaymentMethod) ===
                    getAccountNumber(pm),
                onChange,
              ),
            )}
          </PaymentMethodsGroup>
        </Conditional>
        <Conditional when={others.length}>
          <PaymentMethodsGroup
            showHeading={!!existing.length}
            heading={getText(
              'payApply.paymentMethods.otherPaymentMethodsHeading',
            )}
          >
            <Conditional
              when={hasPendingApplications}
              then={() =>
                renderPendingApplicationOption(pendingSelected, onChange)
              }
            />
            {others.map(pm =>
              renderPaymentMethodOption(
                pm,
                selectedPaymentMethod === pm,
                onChange,
              ),
            )}
          </PaymentMethodsGroup>
        </Conditional>
        {disabledCount > 0 ? (
          <Switch
            id="disabledSwitch"
            text={`Show disabled (${disabledCount})`}
            checked={showDisabled}
            onChange={e => setShowDisabled(e.target.checked)}
          />
        ) : null}
      </Frame>
    </div>
  )
}

PaymentMethodPicker.propTypes = {
  paymentMethods: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  selectedPaymentMethod: PropTypes.object,
  hasPendingApplications: PropTypes.bool,
  pendingSelected: PropTypes.bool,
}

PaymentMethodPicker.defaultProps = {
  selectedPaymentMethod: {},
  hasPendingApplications: false,
  pendingSelected: false,
}

export default PaymentMethodPicker
